import { Cause, Effect, Exit, Ref, Scope } from "effect";
import { Atom, AtomRegistry } from "effect/unstable/reactivity";
import { applyChatProvisional, applyChatRow, emptyChatSession } from "@workerstation/shared/chat-session-model";
import { INTERNAL_HTTP_MESSAGE } from "@workerstation/shared/errors/http";
import type { ChatClientFailure, ChatTail } from "../api/chat/model.ts";
import { ChatApi } from "../api/chat/service.ts";
import { chatFailureMessage } from "../api/chat/failure.ts";
import { AcceptedWork } from "../state/accepted-work.ts";
import { QueryWork } from "../state/query-work.ts";
import { chatResourceKey, type ChatSettlement } from "./model.ts";
import { ChatBrowserIo, type ChatInput } from "./service.ts";
import { initialChatState, type ChatState } from "./state.ts";
import { makeChatQueries, type ChatQuery } from "./query-owner.ts";
type Reply<A> = { readonly _tag: "Success"; readonly value: A } | { readonly _tag: "Failure"; readonly message: string };
const closed = (scope: Scope.Scope): boolean => scope.state._tag === "Closed";
export const makeChatController = Effect.fn("Frontend.Chat.makeSession")(function* (root: Scope.Scope, ports: {
  readonly api: typeof ChatApi.Service; readonly accepted: typeof AcceptedWork.Service;
  readonly queryWork: typeof QueryWork.Service; readonly registry: AtomRegistry.AtomRegistry;
  readonly browser: typeof ChatBrowserIo.Service;
}) {
  const owner = yield* Scope.Scope;
  const { api, accepted, queryWork, registry, browser } = ports;
  const state = yield* Ref.make(initialChatState(Effect.void));
  const failures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
  const receipts = yield* Ref.make<{ readonly total: number; readonly recent: readonly ChatSettlement[] }>({ total: 0, recent: [] });
  const retain = (cause: Cause.Cause<never>) => Ref.update(failures, values => [...values, ...cause.reasons.filter(reason => reason._tag === "Die")]);
  const alive = (current: ChatState) => !current.view.closed && !closed(root) && !closed(owner);
  const read = Ref.get(state).pipe(Effect.map(current => current.view));
  const snapshot = Atom.make(read);
  const publish = Effect.gen(function* () {
    if (!alive(yield* Ref.get(state))) return;
    const exit = yield* Effect.exit(Effect.sync(() => registry.refresh(snapshot)));
    if (Exit.isFailure(exit)) yield* retain(exit.cause);
    return yield* exit;
  });
  const update = (change: (current: ChatState) => ChatState) => Ref.update(state, change).pipe(Effect.andThen(publish));
  const respond = <A>(operation: Effect.Effect<A, ChatClientFailure>): Effect.Effect<Reply<A>> => operation.pipe(
    Effect.match({ onFailure: failure => ({ _tag: "Failure", message: chatFailureMessage(failure) }) as const,
      onSuccess: value => ({ _tag: "Success", value }) as const }),
    Effect.catchCause(cause => Effect.gen(function* () {
      yield* retain(cause);
      if (cause.reasons.every(reason => reason._tag === "Interrupt")) return yield* Effect.interrupt;
      return { _tag: "Failure", message: INTERNAL_HTTP_MESSAGE } as const;
    }))
  );
  yield* Effect.addFinalizer(() => Ref.get(failures).pipe(Effect.flatMap(reasons => reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void)));
  const queries = yield* makeChatQueries(owner, queryWork, retain);
  yield* Effect.addFinalizer(() => Ref.update(state, current => ({ ...current, view: { ...current.view, closed: true } })));
  const currentQuery = (current: ChatState, epoch: number, query: ChatQuery, slot: "configQuery" | "threadsQuery" | "tailQuery" | "timer") =>
    alive(current) && (slot === "configQuery" || slot === "threadsQuery" ? current.stationEpoch === epoch : current.epoch === epoch) && current[slot]?.identity === query.identity;
  const closeQueries = (current: ChatState) => Effect.forEach([current.configQuery, current.threadsQuery, current.tailQuery, current.timer],
    query => query ? query.close : Effect.void, { discard: true });
  const invalidate = Effect.gen(function* () {
    const current = yield* Ref.get(state);
    if (alive(current)) {
      const result = yield* Effect.exit(current.invalidate);
      if (Exit.isFailure(result)) yield* retain(result.cause);
    }
  });
  const applyTail = (current: ChatState, tail: ChatTail): { readonly next: ChatState; readonly changed: boolean } => {
    let session = current.view.session, applied = current.applied, changed = false;
    const merged = new Set(current.merged);
    for (const row of tail.rows) {
      session = applyChatRow(session, row);
      if (row.sequence <= current.applied) continue;
      applied = row.sequence;
      if (row.kind === "proposal_outcome" && row.outcome === "merged") {
        const key = chatResourceKey(current.view.threadId, row.proposalId);
        if (!merged.has(key)) { merged.add(key); changed = true; }
      } else if (row.kind === "tool_result" && row.tool === "workspace_apply_patch" && !row.isError) changed = true;
    }
    session = applyChatProvisional(session, tail.provisional ?? null, tail.streamGeneration);
    return { changed: changed && current.historyLoaded, next: { ...current,
      view: { ...current.view, session, turnActive: tail.provisional?.turnActive ?? false, tailError: null },
      cursor: Math.max(current.cursor, tail.nextAfterSequence), applied, merged, historyLoaded: true } };
  };
  const poll: Effect.Effect<void> = Effect.suspend(() => Effect.gen(function* () {
    const before = yield* Ref.get(state), stationId = before.view.stationId, threadId = before.view.threadId;
    if (!alive(before) || stationId === null || threadId === null || before.tailQuery !== null) return;
    const epoch = before.epoch, generation = before.pollGeneration;
    yield* queries.start(respond(api.tail(stationId, threadId, before.cursor)), (reply, query) => Effect.gen(function* () {
      let changed = false;
      const applied = yield* Ref.modify(state, current => {
        if (!currentQuery(current, epoch, query, "tailQuery") || current.view.threadId !== threadId) return [false, current] as const;
        let next: ChatState = { ...current, tailQuery: null };
        if (current.pollGeneration === generation) {
          if (reply._tag === "Failure") next = { ...next, view: { ...next.view, tailError: reply.message } };
          else { const result = applyTail(next, reply.value); next = result.next; changed = result.changed; }
        }
        return [true, next] as const;
      });
      if (!applied) return;
      yield* publish; if (changed) yield* invalidate;
      const after = yield* Ref.get(state);
      if (after.kickPending) { yield* Ref.update(state, current => ({ ...current, kickPending: false })); yield* poll; }
      else yield* schedule(after.view.turnActive ? 1_000 : 4_000);
    }), query => Ref.modify(state, current => alive(current) && current.epoch === epoch && current.view.threadId === threadId && current.tailQuery === null
      ? [true, { ...current, tailQuery: query }] as const : [false, current] as const));
  }));
  const schedule = (delay: number): Effect.Effect<void> => Effect.gen(function* () {
    const before = yield* Ref.get(state), epoch = before.epoch;
    if (!alive(before) || before.view.threadId === null) return;
    yield* queries.start(Effect.sleep(delay), (_, query) => Effect.gen(function* () {
      const ready = yield* Ref.modify(state, current => currentQuery(current, epoch, query, "timer")
        ? [true, { ...current, timer: null }] as const : [false, current] as const);
      if (ready) yield* poll;
    }), query => Ref.modify(state, current => alive(current) && current.epoch === epoch && current.timer === null
      ? [true, { ...current, timer: query }] as const : [false, current] as const));
  });
  const kick = Effect.gen(function* () {
    const before = yield* Ref.getAndUpdate(state, current => ({ ...current, timer: null, pollGeneration: current.pollGeneration + 1, kickPending: current.tailQuery !== null }));
    if (before.timer) yield* before.timer.close;
    if (before.tailQuery === null) yield* poll;
  });
  const select = (threadId: string | null): Effect.Effect<void> => Effect.gen(function* () {
    const before = yield* Ref.get(state);
    if (!alive(before) || before.view.threadId === threadId) return;
    yield* update(current => ({ ...current, epoch: current.epoch + 1,
      view: { ...current.view, threadId, session: emptyChatSession(), turnActive: false, tailError: null,
        draft: threadId === null ? "" : current.drafts.get(threadId) ?? "", stopping: threadId !== null && current.pending.has(chatResourceKey("stop", threadId)) },
      tailQuery: null, timer: null, cursor: 0, applied: 0, historyLoaded: false, kickPending: false, pollGeneration: 0 }));
    if (before.tailQuery) yield* before.tailQuery.close;
    if (before.timer) yield* before.timer.close;
    yield* poll;
  });
  const finishReceipt = (kind: ChatSettlement["kind"], stationId: string, threadId: string | null, resourceId: string | null, reply: Reply<unknown>) => Effect.gen(function* () {
    const current = yield* Ref.get(state), presented = alive(current) && current.view.stationId === stationId && (threadId === null || current.view.threadId === threadId);
    yield* Ref.update(receipts, current => { const id = current.total + 1; return { total: id, recent: [...current.recent, {
      id, kind, stationId, threadId, resourceId, outcome: reply._tag === "Success" ? "success" : "failure",
      message: reply._tag === "Failure" ? reply.message : null, presented
    } satisfies ChatSettlement].slice(-32) }; });
  });
  const admit = (stationId: string, key: string): Effect.Effect<boolean> => Ref.modify(state, current => !alive(current) || current.view.stationId !== stationId || current.pending.has(key)
    ? [false, current] as const : [true, { ...current, pending: new Set(current.pending).add(key), view: { ...current.view, error: null, threadError: null } }] as const);
  const release = (key: string) => Ref.update(state, current => { const pending = new Set(current.pending); pending.delete(key); return { ...current, pending }; });
  const activeStation = (current: ChatState, stationId: string) => alive(current) && current.view.stationId === stationId;
  const create = (stationId: string): Effect.Effect<void> => accepted.join(Effect.gen(function* () {
    const key = chatResourceKey("create"); if (!(yield* admit(stationId, key))) return;
    yield* update(current => ({ ...current, view: { ...current.view, creating: true } }));
    const reply = yield* respond(api.createThread(stationId));
    yield* release(key);
    const current = yield* Ref.get(state);
    if (activeStation(current, stationId)) {
      if (reply._tag === "Success") {
        yield* update(current => ({ ...current, view: { ...current.view, creating: false, threads: [reply.value, ...current.view.threads.filter(thread => thread.threadId !== reply.value.threadId)] } }));
        yield* select(reply.value.threadId);
      } else yield* update(current => ({ ...current, view: { ...current.view, creating: false, error: reply.message } }));
    }
    yield* finishReceipt("create", stationId, null, null, reply);
  }));
  const receive = (input: ChatInput): Effect.Effect<void> => Effect.gen(function* () {
    const before = yield* Ref.get(state);
    if (!alive(before)) return;
    if (before.view.stationId === input.stationId) { yield* Ref.update(state, current => ({ ...current, invalidate: input.invalidate })); return; }
    const next = initialChatState(input.invalidate), epoch = before.stationEpoch + 1;
    yield* Ref.set(state, { ...next, epoch: before.epoch + 1, stationEpoch: epoch, view: { ...next.view, stationId: input.stationId } });
    yield* publish; yield* closeQueries(before);
    yield* queries.start(respond(api.config(input.stationId)), (reply, query) => Ref.modify(state, current => currentQuery(current, epoch, query, "configQuery")
      ? [true, { ...current, configQuery: null, view: { ...current.view, ...(reply._tag === "Success" ? { config: reply.value } : { error: reply.message }) } }] as const
      : [false, current] as const).pipe(Effect.flatMap(changed => changed ? publish : Effect.void)), query => Ref.modify(state, current => alive(current) && current.stationEpoch === epoch
        ? [true, { ...current, configQuery: query }] as const : [false, current] as const));
    yield* queries.start(respond(api.threads(input.stationId)), (reply, query) => Effect.gen(function* () {
      const before = yield* Ref.get(state); if (!currentQuery(before, epoch, query, "threadsQuery")) return;
      if (reply._tag === "Failure") { yield* update(current => ({ ...current, threadsQuery: null, view: { ...current.view, error: reply.message } })); return; }
      yield* update(current => { const ids = new Set(current.view.threads.map(thread => thread.threadId)); return { ...current, threadsQuery: null,
        view: { ...current.view, threads: [...current.view.threads, ...reply.value.filter(thread => !ids.has(thread.threadId))] } }; });
      if ((yield* Ref.get(state)).view.threadId !== null) return;
      if (reply.value.length === 0) { yield* Effect.forkIn(create(input.stationId), owner); return; }
      const linked = yield* browser.linkedThread;
      yield* select(reply.value.find(thread => thread.threadId === linked)?.threadId ?? reply.value[0].threadId);
    }), query => Ref.modify(state, current => alive(current) && current.stationEpoch === epoch
      ? [true, { ...current, threadsQuery: query }] as const : [false, current] as const));
  });
  return { state, read, snapshot, receipts, receive, select, kick, create, respond, admit, release, update, publish, activeStation, invalidate, finishReceipt, accepted, api, owner };
});
